import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { VersionMessage } from "../../interfaces";
import { LOGOUT_TIME_KEY } from "../../generator/utils/refreshLogoutTimer";
import {
  captureThemeOverrideFromUrl,
  resolveIsDarkMode,
  type ThemeMode,
} from "../../utils/themeOverride";

interface PreferencesSlice {
  theme: string | null;
  origLogo: boolean;
  isDarkMode: boolean;
  /**
   * Dark mode forced by a `theme` param on the login redirect. While set it
   * wins over the user's own setting, so an embedding client can keep our dark
   * mode in step with theirs. Cleared on logout.
   */
  themeOverride: ThemeMode | null;
  logoutTime: number;
  showNotesOnSummary: boolean;
  uppercase: boolean;
  showGDPRModal: boolean;
  versionMessage: VersionMessage | null;
  pathNames: Record<string, string>;
  customThemeUrl?: string;
  globalDraggable: boolean;
}

// Read at module evaluation, i.e. before the router mounts and ProtectedRoute's
// handover redirect drops the query string carrying it.
const themeOverride = captureThemeOverrideFromUrl();

const initialState: PreferencesSlice = {
  theme: localStorage.getItem("theme"),
  customThemeUrl: localStorage.getItem("customThemeUrl") ?? "",
  origLogo: localStorage.getItem("origLogo") !== "false",
  // Seeded from the override so an embedded session does not flash the wrong
  // mode before get-me resolves.
  isDarkMode: themeOverride ? resolveIsDarkMode(themeOverride) : false,
  themeOverride,
  logoutTime: 15 * 60 * 1000,
  showNotesOnSummary: localStorage.getItem("showNotes") === "true",
  uppercase: false,
  showGDPRModal: false,
  versionMessage: null,
  pathNames: {},
  globalDraggable: false,
};

export const preferencesSlice = createSlice({
  name: "preferencesSlice",
  initialState,
  reducers: {
    setPreferences: (
      state,
      action: PayloadAction<{
        theme?: string;
        logoutTime?: string;
        uppercase?: boolean;
        customThemeUrl?: string;
      }>,
    ) => {
      const { theme, logoutTime, uppercase } = action.payload;
      // A URL supplied override is locked for the session - ignore the user's
      // own setting coming back from get-me or a settings save.
      if (theme && !state.themeOverride) {
        state.isDarkMode = resolveIsDarkMode(theme);
      }

      if (logoutTime) {
        const timeInMs = Number(logoutTime) * 60 * 1000;
        state.logoutTime = timeInMs;
        localStorage.setItem(LOGOUT_TIME_KEY, logoutTime);
      }

      if (typeof uppercase === "boolean") {
        state.uppercase = uppercase;
      }
    },
    setPathNames: (state, action: PayloadAction<Record<string, string>>) => {
      state.pathNames = action.payload;
    },

    setShowNotesOnSummary: (state, action) => {
      state.showNotesOnSummary = action.payload;
    },
    setShowGDPRModal: (state, action) => {
      state.showGDPRModal = action.payload;
    },
    setTheme: (state, action) => {
      const { theme, customThemeUrl, origLogo } = action.payload;
      state.theme = theme;
      state.customThemeUrl = customThemeUrl;
      state.origLogo = origLogo;

      // Persisted so the branding (and its flags) are available on the login
      // screen and immediately on reload, before get-me resolves.
      if (theme) {
        localStorage.setItem("theme", theme);
      } else {
        localStorage.removeItem("theme");
      }
      if (customThemeUrl) {
        localStorage.setItem("customThemeUrl", customThemeUrl);
      } else {
        localStorage.removeItem("customThemeUrl");
      }
      localStorage.setItem("origLogo", String(origLogo !== false));
    },
    setVersionMessage: (
      state,
      action: PayloadAction<VersionMessage | null>,
    ) => {
      state.versionMessage = action.payload;
    },
    setGlobalDraggable: (state, action: PayloadAction<boolean>) => {
      state.globalDraggable = action.payload;
    },
  },
});

export const {
  setTheme,
  setPreferences,
  setShowNotesOnSummary,
  setShowGDPRModal,
  setVersionMessage,
  setPathNames,
  setGlobalDraggable,
} = preferencesSlice.actions;

export default preferencesSlice.reducer;
